/**
 * @lines98/client/lib/daily-finish — shared daily submit + cache-discipline module.
 *
 * Single source for «submit a finished daily game, then update the local cache
 * by outcome». Reused by every daily-finish path so the cache discipline can't
 * drift between them (owner-directive 2026-05-30: shared behavior lives in a
 * module, not copied per screen):
 *   - board-screen `finalizeGame` (board_full / player_finish / rollover-trigger-A).
 *   - result-screen `retryDailySubmit` (manual retry after network/rate_limited).
 *   - daily-board-screen boot trigger B (brought-from-yesterday rollover submit).
 *
 * **Cache discipline (load-bearing — anti-cheat.md §3.5 + core-daily-board.md
 * AC-13 + §7.1).** A daily attempt is "consumed" only when the server's atomic
 * `daily_attempts` pre-claim succeeds:
 *   - `verified` / `duplicate_attempt` / `rejected` (post-pre-claim) → the attempt
 *     IS consumed → `savePlayed` + `clearInProgress` (done-state UX on next mount).
 *   - `rate_limited` / `engine_mismatch` / `network` → NOT consumed (server rejected
 *     before pre-claim, or never reached it) → do NOT cache `played`; keep the
 *     in-progress record so the game survives for a later retry (§7.1 offline).
 *
 * **Offline / retry (§7.1 + anti-cheat.md §7.12).** On a non-consuming failure the
 * in-progress record stays, so the next boot of the daily screen re-detects it: a
 * today-record resumes (live recovery); a past-day record (rollover) re-submits via
 * trigger B (`submitBroughtFromYesterday`). `rollover` games can wait in the cache
 * up to `LATE_SUBMIT_WINDOW_HOURS` (server-side acceptance window).
 *
 * Cross-references:
 *   - anti-cheat.md §3.5 (consumed-vs-not), §7.1 (network retry), §7.12 (late submit).
 *   - core-daily-board.md §7.3 (return-next-day rollover), AC-12, AC-13.
 *   - services/game-submit.ts (SubmitResult union + the consumed/not split).
 *   - services/daily-cache.ts (savePlayed / clearInProgress / loadBroughtFromYesterday).
 */

import { replayScore } from '@lines98/shared';
import { dateOfDayFromUtc } from '@lines98/shared/util';
import type { Move } from '@lines98/shared/types/game';

import {
  clearInProgress,
  savePlayed,
  type FinalBoardSnapshot,
  type InProgressRecord,
} from '../services/daily-cache.js';
import {
  submitFinishedDailyGame,
  type SubmitResult,
} from '../services/game-submit.js';

/**
 * Extract the replay `short_id` from a `replay_url` of the form `/r/<short_id>`
 * (ADR-0003). The done-state caches it so the same-device path can link to /
 * re-simulate the own replay without a leaderboard round-trip. Returns `null`
 * for an empty / dev (`#dev`) / malformed url.
 */
function parseShortId(replayUrl: string | undefined): string | null {
  if (!replayUrl) return null;
  const tail = replayUrl.split('/').pop() ?? '';
  return /^[0-9A-Za-z]{8,32}$/.test(tail) ? tail : null;
}

/**
 * Apply the post-submit cache discipline for a given outcome. Pure side-effect on
 * the daily cache — no UI. Returns nothing; callers read the `SubmitResult` they
 * passed in for UI branching.
 *
 * @param dateOfDay — the attempt's `date_of_day` (today for trigger A, the past
 *   day for trigger B). Used as the `played` cache key.
 * @param result — the server submit outcome.
 * @param finalBoard — optional final-board snapshot captured at finish-time (the
 *   live callers — board-screen finalizeGame / result-screen retry — have the
 *   store's final state; the rollover-from-yesterday path omits it). Persisted on
 *   the `verified` path so the daily done-state renders the final board instantly
 *   on the same device (core-daily-board.md §2.3-DONE). Cache miss → re-simulation.
 * @param options.currentDateOfDay — TODAY's `date_of_day` (server-authoritative;
 *   defaults to the local clock's day if omitted). Used to decide whether a
 *   consumed attempt belongs to TODAY (→ write the `played` done-state) or a PAST
 *   day (→ drop the stale in-progress record ONLY, never write a foreign-day
 *   `played` record). See the past-day cache-leak fix below.
 */
export async function applyDailyCacheDiscipline(
  dateOfDay: string,
  result: SubmitResult,
  finalBoard?: FinalBoardSnapshot,
  options: { currentDateOfDay?: string } = {},
): Promise<void> {
  // Is the attempt being resolved TODAY's daily, or a stale PAST-day attempt that
  // a rollover left in the cache (trigger B / a watch that fired at the boundary)?
  // The `played` cache is a single-key, NON-date-segregated store (services/
  // daily-cache PLAYED_KEY + the localStorage mirror keyed only by playerId). So
  // writing a `played` record for a PAST day would (a) clobber / churn the cache
  // for an irrelevant day — `loadPlayed(today)` immediately discards it as stale,
  // and (b) for the rollover-resubmit path, fire on EVERY entry until cleared,
  // showing a misleading «closed by timer» done-state. A past day that's already
  // resolved «уже не имеет практического значения для текущей игровой сессии»
  // (anti-cheat.md §7.12) — so for it we DROP the stale in-progress record and
  // write NOTHING to `played`. Only TODAY's consumed attempt earns a done-state.
  const today = options.currentDateOfDay ?? dateOfDayFromUtc(Date.now());
  const isToday = dateOfDay === today;

  if (result.status === 'verified') {
    if (isToday) {
      await savePlayed({
        dateOfDay,
        scoreVerified: result.scoreVerified,
        replayId: result.gameId,
        linesVerified: result.linesVerified,
        shortId: parseShortId(result.replayUrl),
        ...(finalBoard !== undefined ? { finalBoard } : {}),
        timestamp: Date.now(),
      });
    }
    // Consumed (verified) → the attempt is resolved server-side; drop the live
    // record. For a past-day verified rollover we skip the foreign-day `played`
    // write above but STILL clear the stale record (it must not re-submit).
    await clearInProgress();
    return;
  }
  if (result.status === 'duplicate_attempt' || result.status === 'rejected') {
    // Server pre-claimed the attempt (or already had one), OR late-rejected a
    // past-day rollover (`reason:'unknown'` per finish-game.ts late-submit guard)
    // → consumed / un-creditable. For TODAY: save a minimal played stub so the
    // daily screen shows done-state next mount. For a PAST day (the stale-attempt
    // leak): write NOTHING — just drop the stale in-progress record so it stops
    // re-submitting on every entry and never pollutes today's cache.
    if (isToday) {
      await savePlayed({ dateOfDay, scoreVerified: 0, replayId: null, timestamp: Date.now() });
    }
    await clearInProgress();
    return;
  }
  // rate_limited / engine_mismatch / network → NOT consumed. Keep in-progress so
  // the game survives for a retry; do NOT cache `played` (would falsely show
  // «уже сыграно» while the server still treats the attempt as available).
  //
  // Exception — a PAST-day attempt that fails to even reach a verdict: it can only
  // legally re-submit within `LATE_SUBMIT_WINDOW_HOURS` of its own day. Within the
  // window we keep retrying (offline resilience, §7.1). BUT a transient failure on
  // a long-stale past-day record (well beyond the window) would otherwise retry
  // forever on every entry. The window math lives server-side; the client can't
  // recompute it without the rollover-boundary constant, so we lean on the server
  // verdict (the late-submit guard fires BEFORE the network can flake on a healthy
  // server) and leave non-verdict transients alone — they self-resolve once the
  // server is reachable and returns the `unknown` late-reject (handled above).
}

/**
 * Re-simulate a brought-from-yesterday in-progress record and submit it as a
 * `finish_reason='rollover'` game for ITS OWN past day (trigger B,
 * core-daily-board.md §7.3 + AC-12). Returns the submit outcome so the caller can
 * surface a toast / branch UX; the cache discipline is already applied here.
 *
 * Re-simulation via the canonical engine (`replayScore`) recovers the final
 * verified score for the toast without re-driving the live store — the same
 * engine the server uses, so the numbers agree (anti-cheat invariant). If the
 * cached moves don't replay cleanly (corruption / engine drift), we submit with a
 * 0 score_claimed — the server re-simulates authoritatively anyway and either
 * accepts (publishing its own verified score) or rejects; either way the attempt
 * is resolved and the stale record is cleared.
 *
 * @param currentDateOfDay — TODAY's server-authoritative `date_of_day` (from
 *   `GET /daily/seed`). Threaded to the cache discipline so the consumed past-day
 *   attempt is dropped from the cache WITHOUT writing a foreign-day `played`
 *   record (the stale-attempt leak fix — see `applyDailyCacheDiscipline`).
 * @returns `{ result, score }` — the submit outcome + the (best-effort) verified
 *   score recovered from local re-simulation for toast display.
 */
export async function submitBroughtFromYesterday(
  record: InProgressRecord,
  currentDateOfDay: string,
): Promise<{ result: SubmitResult; score: number }> {
  const moves: Move[] = record.moves.slice();
  const replay = replayScore(record.seed, moves);
  const localScore = replay.valid ? replay.score : 0;

  let result: SubmitResult;
  try {
    result = await submitFinishedDailyGame({
      context: { kind: 'daily', dateOfDay: record.dateOfDay },
      seed: record.seed,
      moves,
      scoreClaimed: localScore,
      linesClaimed: replay.valid ? replay.lines : 0,
      finishReason: 'rollover',
      mode: 'predict',
    });
  } catch {
    result = { status: 'network', attempt: 0 };
  }

  // The record's day is a PAST day (loadBroughtFromYesterday guarantees
  // record.dateOfDay !== currentDateOfDay) — pass today so the discipline drops
  // the stale record on a consumed verdict WITHOUT writing a past-day `played`.
  await applyDailyCacheDiscipline(record.dateOfDay, result, undefined, { currentDateOfDay });

  // On a verified accept the server's score is canonical; prefer it for the toast.
  const score = result.status === 'verified' ? result.scoreVerified : localScore;
  return { result, score };
}
